import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingBag } from 'lucide-react';

export const HeroSection = () => {
  return (
    <section className="pt-24 md:pt-32 pb-16 md:pb-24 bg-[#FDFCF8] relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#D4C5A9]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#4A5D4F]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Copy */}
          <div>
            <div className="inline-flex items-center gap-2 bg-[#4A5D4F]/10 px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-[#4A5D4F] rounded-full animate-pulse" />
              <span className="text-sm font-medium text-[#4A5D4F]">Ramadan & Eid Geschenke 2026</span>
            </div>

            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C332E] mb-6 leading-[1.1]">
              Einzigartige Geschenke, die von Herzen kommen
            </h1>

            <p className="text-lg text-[#6B7280] leading-relaxed mb-8 max-w-lg">
              Handverlesene Eid-Geschenke, liebevoll verpackt und schnell geliefert.
              Mach deinen Liebsten eine Freude, die sie nie vergessen.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to="/geschenke"
                className="inline-flex items-center gap-2 bg-[#4A5D4F] text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-[#3A493E] transition-all hover:shadow-xl hover:-translate-y-0.5"
                data-testid="hero-cta-primary"
              >
                <ShoppingBag className="w-5 h-5" />
                Jetzt Geschenk entdecken
              </Link>
              <Link
                to="/ratgeber"
                className="inline-flex items-center gap-2 border-2 border-[#2C332E]/20 text-[#2C332E] px-8 py-4 rounded-full font-medium hover:border-[#4A5D4F] hover:text-[#4A5D4F] transition-all"
                data-testid="hero-cta-secondary"
              >
                Geschenk-Ratgeber <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Trust Micro-Badges */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#6B7280]">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-[#4A5D4F]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                Sichere Zahlung
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-[#4A5D4F]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                Schneller Versand
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-[#4A5D4F]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                Liebevoll verpackt
              </span>
            </div>
          </div>

          {/* Right - Featured Products Grid */}
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <Link to="/geschenke" className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="aspect-square bg-[#F5F5F0] overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_5a103fd5-3095-43df-87fa-8511287ae247/artifacts/x7bdty5j_image.png" alt="Eid Mubarak Geschenkbox" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" />
              </div>
            </Link>
            <Link to="/geschenke" className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="aspect-square bg-[#F5F5F0] overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_5a103fd5-3095-43df-87fa-8511287ae247/artifacts/c69bbdr1_image.png" alt="Personalisierte Schmuckboxen" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" />
              </div>
            </Link>
            <Link to="/geschenke" className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="aspect-square bg-[#F5F5F0] overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_5a103fd5-3095-43df-87fa-8511287ae247/artifacts/4o3ioo52_image.png" alt="Eid Mubarak Geldkarte für Kinder" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" />
              </div>
            </Link>
            <Link to="/geschenke" className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="aspect-square bg-[#F5F5F0] overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_5a103fd5-3095-43df-87fa-8511287ae247/artifacts/6wosmjuq_image.png" alt="Dekorative Gebetsnische" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
